import React, { useContext } from 'react';
import styled from 'styled-components';
import { useScrollPosition } from '@n8tb1t/use-scroll-position';
import {
  IconMenu,
  IconSearch,
  PopoverTrigger,
  IconChevronDown,
  IconChevronUp,
  PopoverNavigation,
  PopoverNavigationLink,
  Backdrop,
} from '@spotify-internal/encore-web';
import { useTracking } from '../../Tracking';
import { RequireFeatureFlag } from '../../Auth/RequireFeatureFlag';
import { useCurrentUser } from '../../../@mrkt/useCurrentUser';
import { useLayoutContext } from '../LayoutState';
import { UserState } from './types';
import { ResponsiveLogo, Modal } from '../../ui';
import { useScrollYPosition } from '../../utils';
import { NavSideMenu } from './NavSideMenu';
import { OpenSideMenuButton } from './NavSideMenu.styles';
import {
  NavBarWrapper,
  LogoArea,
  LinksArea,
  ActionsArea,
  MenuButtonArea,
  LoginButton,
  ClaimButton,
  MenuLink,
  MenuCategory,
  StyledListItemWithMargin,
  StyledListItemNoMargin,
  StyledPopoverNavigationItem,
  MenuLinkWrapper,
} from './NavBar.styles';
import { USE_SEARCH } from './constants';
import { isHelpLive } from '../../Help/util';
import { useRouter } from 'next/router';
import { useT } from '@spotify-internal/i18n-core';
import { NavBarLanguageItem } from './Language';
import { NavArrowIcon } from './utils';
import { Wait } from '../Wait';
import { FrodorModulesContext, Link } from '@spotify-internal/frodor-modules';
import { login, logout } from '../../../../features/src/auth';

type MenuState = {
  features?: boolean;
  resources?: boolean;
  help?: boolean;
};

type NavBarProps = {
  forceState?: UserState;
  defaultMenuState?: MenuState;
};

const SIDE_MENU_TRIGGER_BREAKPOINT = 992;
const TRACK_CAT = 'Top Nav';
const MENU_CLOSE_DELAY = 500;

const StyledPopoverNavigation = styled(PopoverNavigation)`
  width: 173px;
  [dir='rtl'] & * {
    direction: rtl;
    & > svg {
      transform: rotateY(180deg);
    }
  }
`;

const ExternalLinkLabel = styled.div`
  display: flex;
  justify-content: space-between;
`;

const menuTimeouts: { [key: string]: NodeJS.Timeout } = {};

export const NavBar: React.FC<NavBarProps> = ({
  forceState = null,
  defaultMenuState = {},
}) => {
  const t = useT();
  const { sendEvent } = useTracking();
  const layoutContext = useLayoutContext();
  const { navBarBackground, navBarTextColor, navBarBleed } = layoutContext;
  const user = useCurrentUser();
  const [userState, setUserState] = React.useState<UserState>('loading');
  const [menuState, setMenuState] = React.useState<MenuState>(defaultMenuState);
  const [sideMenuOpen, setSideMenuOpened] = React.useState(false);
  const [showNavBarMobile, setShowNavBarMobile] = React.useState(true);
  const scrollPos = useScrollYPosition();
  const mounted = React.useRef(true);
  const router = useRouter();

  useScrollPosition(({ prevPos, currPos }) =>
    setShowNavBarMobile(currPos.y > prevPos.y || currPos.y > -1),
  );

  const openSideMenu = React.useCallback(() => {
    document.body.classList.add('sidepanel-active');
    window.addEventListener('keydown', handleKey, true); // eslint-disable-line no-use-before-define
    window.addEventListener('resize', handleResize, false); // eslint-disable-line no-use-before-define
    sendEvent({
      eventCategory: `Frodor - ${TRACK_CAT}`,
      eventAction: 'click',
      eventLabel: 'Open Menu - side',
    });
    setSideMenuOpened(true);
  }, []);

  React.useEffect(() => {
    // focus side menu when opened
    if (sideMenuOpen) {
      const el = document.getElementById('nav-sidemenu');
      el?.focus();
    }
  }, [sideMenuOpen]);

  const closeSideMenu = React.useCallback(() => {
    document.body.classList.remove('sidepanel-active');
    window.removeEventListener('keydown', handleKey, false); // eslint-disable-line no-use-before-define
    window.removeEventListener('resize', handleResize, false); // eslint-disable-line no-use-before-define
    sendEvent({
      eventCategory: `Frodor - ${TRACK_CAT}`,
      eventAction: 'click',
      eventLabel: 'Close Menu - side',
    });
    setSideMenuOpened(false);
  }, []);

  const openMenu = (key: keyof MenuState) => {
    setMenuState({ [key]: true }); // closes all other menus
    sendEvent({
      eventCategory: `Frodor - ${TRACK_CAT}`,
      eventAction: 'click',
      eventLabel: `Open Menu - ${key}`,
    });
  };

  const closeMenu = (key: keyof MenuState) => {
    if (!mounted.current) return;

    setMenuState(curState => ({ ...curState, [key]: false }));
    sendEvent({
      eventCategory: `Frodor - ${TRACK_CAT}`,
      eventAction: 'click',
      eventLabel: `Close Menu - ${key}`,
    });
  };

  // event handlers for dropdown menus
  const getMenuHandlers = (key: keyof MenuState) => {
    return {
      onMouseEnter: () => {
        // open menu and clear timeouts
        const to = menuTimeouts[key];
        if (to) {
          clearTimeout(to);
          delete menuTimeouts[key];
        }
        openMenu(key);
      },
      onMouseLeave: () => {
        // close menu on delay for usability
        menuTimeouts[key] = setTimeout(() => closeMenu(key), MENU_CLOSE_DELAY);
      },
      onKeyDown: (e: React.KeyboardEvent) => {
        if (e.key === ' ') {
          e.preventDefault();
          if (menuState[key]) {
            closeMenu(key);
          } else {
            openMenu(key);
          }
        }
      },
      onClick: () => {
        if (!menuState[key]) {
          openMenu(key);
        }
      },
    };
  };

  React.useEffect(() => {
    const handleChangeRouter = () => {
      closeSideMenu();
    };
    router.events.on('routeChangeStart', handleChangeRouter);
    return () => {
      router.events.off('routeChangeStart', handleChangeRouter);
    };
  }, [router.events, closeSideMenu]);

  React.useEffect(() => {
    return () => {
      document.body.classList.remove('sidepanel-active');
      mounted.current = false;
    };
  }, []);

  // set state of nav bar based on current user, or use 'forceState' if specified
  React.useEffect(() => {
    if (forceState) {
      setUserState(forceState);
      return;
    }
    setUserState(
      user
        ? (user.hasAccess && 'claimed') || 'unclaimed'
        : (user === undefined && 'loading') || 'loggedOut',
    );
  }, [user]);

  const handleKey = React.useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      closeSideMenu();
    }
  }, []);

  const handleResize = React.useCallback(() => {
    if (window.innerWidth >= SIDE_MENU_TRIGGER_BREAKPOINT) {
      closeSideMenu();
    }
  }, []);

  const { isFrodor, onLinkClick } = useContext(FrodorModulesContext);
  const getInternalHref = (href: string) =>
    isFrodor ? href : 'https://artists.spotify.com'.concat(href);

  const showLogin = userState === 'loggedOut';
  const showLogout = userState === 'claimed' || userState === 'unclaimed';
  const showDashboard = userState === 'claimed';
  const showClaim = userState === 'unclaimed' || userState === 'loggedOut';

  return userState !== 'loading' ? (
    <NavBarWrapper
      customBackground={navBarBackground}
      customTextColor={navBarTextColor}
      bleed={navBarBleed}
      isScrolling={scrollPos !== 0}
      isScrollingUp={showNavBarMobile}
      data-testid={`navbar-state-${userState}`}
    >
      <LogoArea>
        <Link
          track={[TRACK_CAT, 'Logo']}
          href={getInternalHref('/home/?ref=logo')}
          useInternalNavigation={isFrodor}
          dataTestId="navigation-logo"
        >
          <ResponsiveLogo />
        </Link>
      </LogoArea>

      <LinksArea>
        <ul id="menu-links">
          <li>
            <MenuLinkWrapper>
              <MenuLink
                track={[TRACK_CAT, 'GetStarted']}
                href={getInternalHref('/get-started')}
                useInternalNavigation={isFrodor}
                dataTestId="navigation-get-started"
              >
                {t('FRODOR_NAVBAR_SIDE_GET_STARTED', 'Get started', '')}
              </MenuLink>
            </MenuLinkWrapper>
          </li>
          {/* Features menu */}
          <li>
            <MenuCategory>
              <div
                data-testid="navigation-menu-features"
                {...getMenuHandlers('features')}
              >
                <PopoverTrigger
                  onShow={() => {}}
                  onHide={() => {}}
                  placement={PopoverTrigger.bottom}
                  overlay={
                    menuState.features && (
                      <StyledPopoverNavigation arrow={PopoverNavigation.top}>
                        <StyledPopoverNavigationItem>
                          <PopoverNavigationLink
                            href={
                              isFrodor
                                ? undefined
                                : getInternalHref('/features')
                            }
                            component={isFrodor ? 'button' : 'a'}
                            data-testid="navigation-features-overview"
                            onClick={() =>
                              onLinkClick({
                                href: getInternalHref('/features'),
                                external: false,
                                trackingAttributes: [
                                  TRACK_CAT,
                                  'features-overview',
                                ],
                              })
                            }
                          >
                            {t(
                              'FRODOR_NAVBAR_FEATURES_OVERVIEW',
                              'Overview',
                              '',
                            )}
                          </PopoverNavigationLink>
                        </StyledPopoverNavigationItem>
                        <StyledPopoverNavigationItem features>
                          <PopoverNavigationLink
                            href="https://canvas.spotify.com/?utm_campaign=owned_s4a_awr_all_clips_crossdevice__global-s4a-nav&utm_medium=owned&utm_source=s4a"
                            data-testid="navigation-canvas"
                            target="_blank"
                            onClick={() =>
                              onLinkClick({
                                href: 'https://canvas.spotify.com/?utm_campaign=owned_s4a_awr_all_clips_crossdevice__global-s4a-nav&utm_medium=owned&utm_source=s4a',
                                external: true,
                                trackingAttributes: [TRACK_CAT, 'canvas'],
                              })
                            }
                          >
                            <ExternalLinkLabel>
                              Canvas <NavArrowIcon />
                            </ExternalLinkLabel>
                          </PopoverNavigationLink>
                        </StyledPopoverNavigationItem>
                        <StyledPopoverNavigationItem features>
                          <PopoverNavigationLink
                            href="https://clips.byspotify.com/?utm_campaign=owned_s4a_awr_all_clips_crossdevice__global-s4a-nav&utm_medium=owned&utm_source=s4a"
                            data-testid="navigation-clips"
                            target="_blank"
                            onClick={() =>
                              onLinkClick({
                                href: 'https://clips.byspotify.com/?utm_campaign=owned_s4a_awr_all_clips_crossdevice__global-s4a-nav&utm_medium=owned&utm_source=s4a',
                                external: true,
                                trackingAttributes: [TRACK_CAT, 'clips'],
                              })
                            }
                          >
                            <ExternalLinkLabel>
                              Clips <NavArrowIcon />
                            </ExternalLinkLabel>
                          </PopoverNavigationLink>
                        </StyledPopoverNavigationItem>
                        <StyledPopoverNavigationItem features>
                          <PopoverNavigationLink
                            href={
                              isFrodor
                                ? undefined
                                : getInternalHref('/discovery-mode')
                            }
                            component={isFrodor ? 'button' : 'a'}
                            data-testid="navigation-discovery-mode"
                            onClick={() =>
                              onLinkClick({
                                href: getInternalHref('/discovery-mode'),
                                external: false,
                                trackingAttributes: [
                                  TRACK_CAT,
                                  'discovery-mode',
                                ],
                              })
                            }
                          >
                            Discovery Mode
                          </PopoverNavigationLink>
                        </StyledPopoverNavigationItem>
                        <StyledPopoverNavigationItem features>
                          <PopoverNavigationLink
                            href={
                              isFrodor ? undefined : getInternalHref('/marquee')
                            }
                            component={isFrodor ? 'button' : 'a'}
                            data-testid="navigation-marquee"
                            onClick={() =>
                              onLinkClick({
                                href: getInternalHref('/marquee'),
                                external: false,
                                trackingAttributes: [TRACK_CAT, 'marquee'],
                              })
                            }
                          >
                            Marquee
                          </PopoverNavigationLink>
                        </StyledPopoverNavigationItem>
                        <StyledPopoverNavigationItem features>
                          <PopoverNavigationLink
                            href={
                              isFrodor ? undefined : getInternalHref('/merch')
                            }
                            component={isFrodor ? 'button' : 'a'}
                            data-testid="navigation-merch"
                            onClick={() =>
                              onLinkClick({
                                href: getInternalHref('/merch'),
                                external: false,
                                trackingAttributes: [TRACK_CAT, 'merch'],
                              })
                            }
                          >
                            {t('FRODOR_NAVBAR_MERCH_LINK', 'Merch', '')}
                          </PopoverNavigationLink>
                        </StyledPopoverNavigationItem>
                        <StyledPopoverNavigationItem features>
                          <PopoverNavigationLink
                            href={
                              isFrodor
                                ? undefined
                                : getInternalHref('/playlisting')
                            }
                            component={isFrodor ? 'button' : 'a'}
                            data-testid="navigation-playlisting"
                            onClick={() =>
                              onLinkClick({
                                href: getInternalHref('/playlisting'),
                                external: false,
                                trackingAttributes: [TRACK_CAT, 'playlisting'],
                              })
                            }
                          >
                            {t(
                              'FRODOR_NAVBAR_PLAYLISTING_LINK',
                              'Playlisting',
                              '',
                            )}
                          </PopoverNavigationLink>
                        </StyledPopoverNavigationItem>
                        <StyledPopoverNavigationItem features>
                          <PopoverNavigationLink
                            href="https://promocards.byspotify.com/artists"
                            data-testid="navigation-promocards"
                            target="_blank"
                            onClick={() =>
                              onLinkClick({
                                href: getInternalHref(
                                  'https://promocards.byspotify.com/artists',
                                ),
                                external: true,
                                trackingAttributes: [TRACK_CAT, 'promocards'],
                              })
                            }
                          >
                            <ExternalLinkLabel>
                              {t(
                                'FRODOR_NAVBAR_PROMO_CARDS',
                                'Promo Cards',
                                'Promo is short for promotional',
                              )}
                              <NavArrowIcon />
                            </ExternalLinkLabel>
                          </PopoverNavigationLink>
                        </StyledPopoverNavigationItem>
                      </StyledPopoverNavigation>
                    )
                  }
                >
                  <MenuLinkWrapper>
                    <MenuLink
                      component="button"
                      href=""
                      onClick={() => {}}
                      track={['', '']}
                    >
                      <span>{t('FRODOR_NAVBAR_FEATURES', 'Features', '')}</span>
                      {menuState.features ? (
                        <IconChevronUp
                          iconSize={16}
                          aria-label={t(
                            'FRODOR_NAVBAR_CLOSE_FEATURES',
                            'Close features menu',
                            '',
                          )}
                        />
                      ) : (
                        <IconChevronDown
                          iconSize={16}
                          aria-label={t(
                            'FRODOR_NAVBAR_OPEN_FEATURES',
                            'Open features menu',
                            '',
                          )}
                        />
                      )}
                    </MenuLink>
                  </MenuLinkWrapper>
                </PopoverTrigger>
              </div>
            </MenuCategory>
          </li>

          {/* Resources menu */}
          <li>
            <MenuCategory>
              <div
                data-testid="navigation-menu-resources"
                {...getMenuHandlers('resources')}
              >
                <PopoverTrigger
                  onShow={() => {}}
                  onHide={() => {}}
                  placement={PopoverTrigger.bottom}
                  overlay={
                    menuState.resources && (
                      <StyledPopoverNavigation arrow={PopoverNavigation.top}>
                        <StyledPopoverNavigationItem>
                          <PopoverNavigationLink
                            href={
                              isFrodor ? undefined : getInternalHref('/videos')
                            }
                            component={isFrodor ? 'button' : 'a'}
                            data-testid="navigation-videos"
                            onClick={() =>
                              onLinkClick({
                                href: getInternalHref('/videos'),
                                external: false,
                                trackingAttributes: [TRACK_CAT, 'Videos'],
                              })
                            }
                          >
                            {t('FRODOR_NAVBAR_VIDEOS', 'Videos', '')}{' '}
                          </PopoverNavigationLink>
                        </StyledPopoverNavigationItem>
                        <StyledPopoverNavigationItem>
                          <PopoverNavigationLink
                            href={
                              isFrodor ? undefined : getInternalHref('/blog')
                            }
                            component={isFrodor ? 'button' : 'a'}
                            data-testid="navigation-blog"
                            onClick={() =>
                              onLinkClick({
                                href: getInternalHref('/blog'),
                                external: false,
                                trackingAttributes: [TRACK_CAT, 'Blog'],
                              })
                            }
                          >
                            {t('FRODOR_NAVBAR_NEWS', 'News', '')}
                          </PopoverNavigationLink>
                        </StyledPopoverNavigationItem>
                        <StyledPopoverNavigationItem>
                          <PopoverNavigationLink
                            href={
                              isFrodor ? undefined : getInternalHref('/events')
                            }
                            component={isFrodor ? 'button' : 'a'}
                            data-testid="navigation-events"
                            onClick={() =>
                              onLinkClick({
                                href: getInternalHref('/events'),
                                external: false,
                                trackingAttributes: [TRACK_CAT, 'Events'],
                              })
                            }
                          >
                            {t('FRODOR_NAVBAR_EVENTS', 'Events', '')}
                          </PopoverNavigationLink>
                        </StyledPopoverNavigationItem>
                        <StyledPopoverNavigationItem>
                          <PopoverNavigationLink
                            href="https://infocus.byspotify.com?utm_campaign=top-nav&utm_medium=owned-product&utm_source=s4a"
                            target="_blank"
                            data-testid="navigation-infocus"
                            onClick={() =>
                              onLinkClick({
                                href: 'https://infocus.byspotify.com?utm_campaign=top-nav&utm_medium=owned-product&utm_source=s4a',
                                external: true,
                                trackingAttributes: [TRACK_CAT, 'In Focus'],
                              })
                            }
                          >
                            <ExternalLinkLabel>
                              In Focus <NavArrowIcon />
                            </ExternalLinkLabel>
                          </PopoverNavigationLink>
                        </StyledPopoverNavigationItem>
                        <StyledPopoverNavigationItem>
                          <PopoverNavigationLink
                            href="https://fanstudy.byspotify.com?utm_campaign=top-nav&utm_medium=owned-product&utm_source=s4a"
                            target="_blank"
                            data-testid="navigation-fanstudy"
                            onClick={() =>
                              onLinkClick({
                                href: 'https://fanstudy.byspotify.com?utm_campaign=top-nav&utm_medium=owned-product&utm_source=s4a',
                                external: true,
                                trackingAttributes: [TRACK_CAT, 'Fan Study'],
                              })
                            }
                          >
                            <ExternalLinkLabel>
                              Fan Study <NavArrowIcon />
                            </ExternalLinkLabel>
                          </PopoverNavigationLink>
                        </StyledPopoverNavigationItem>
                        <StyledPopoverNavigationItem>
                          <PopoverNavigationLink
                            href="https://found.byspotify.com?utm_campaign=top-nav&utm_medium=owned-product&utm_source=s4a"
                            target="_blank"
                            data-testid="navigation-madetobefound"
                            onClick={() =>
                              onLinkClick({
                                href: 'https://found.byspotify.com?utm_campaign=top-nav&utm_medium=owned-product&utm_source=s4a',
                                external: true,
                                trackingAttributes: [
                                  TRACK_CAT,
                                  'Made to Be Found',
                                ],
                              })
                            }
                          >
                            <ExternalLinkLabel>
                              Made to Be Found <NavArrowIcon />
                            </ExternalLinkLabel>
                          </PopoverNavigationLink>
                        </StyledPopoverNavigationItem>
                        <StyledPopoverNavigationItem>
                          <PopoverNavigationLink
                            href="https://loudandclear.byspotify.com?utm_campaign=top-nav&utm_medium=owned-product&utm_source=s4a"
                            target="_blank"
                            data-testid="navigation-loudclear"
                            onClick={() =>
                              onLinkClick({
                                href: 'https://loudandclear.byspotify.com?utm_campaign=top-nav&utm_medium=owned-product&utm_source=s4a',
                                external: true,
                                trackingAttributes: [TRACK_CAT, 'Loud & Clear'],
                              })
                            }
                          >
                            <ExternalLinkLabel>
                              Loud & Clear <NavArrowIcon />
                            </ExternalLinkLabel>
                          </PopoverNavigationLink>
                        </StyledPopoverNavigationItem>
                        <StyledPopoverNavigationItem>
                          <PopoverNavigationLink
                            href="https://noteable.spotify.com?utm_campaign=top-nav&utm_medium=owned-product&utm_source=s4a"
                            target="_blank"
                            data-testid="navigation-noteable"
                            onClick={() =>
                              onLinkClick({
                                href: 'https://noteable.spotify.com?utm_campaign=top-nav&utm_medium=owned-product&utm_source=s4a',
                                external: true,
                                trackingAttributes: [TRACK_CAT, 'Noteable'],
                              })
                            }
                          >
                            <ExternalLinkLabel>
                              Noteable <NavArrowIcon />
                            </ExternalLinkLabel>
                          </PopoverNavigationLink>
                        </StyledPopoverNavigationItem>
                      </StyledPopoverNavigation>
                    )
                  }
                >
                  <MenuLinkWrapper>
                    <MenuLink
                      component="button"
                      href=""
                      onClick={() => {}}
                      track={['', '']}
                    >
                      {t('FRODOR_NAVBAR_RESOURCES', 'Resources', '')}
                      {menuState.resources ? (
                        <IconChevronUp
                          iconSize={16}
                          aria-label={t(
                            'FRODOR_NAVBAR_RESOURCES_CLOSE',
                            'Close resources menu',
                            '',
                          )}
                        />
                      ) : (
                        <IconChevronDown
                          iconSize={16}
                          aria-label={t(
                            'FRODOR_NAVBAR_RESOURCES_OPEN',
                            'Open resources menu',
                            '',
                          )}
                        />
                      )}
                    </MenuLink>
                  </MenuLinkWrapper>
                </PopoverTrigger>
              </div>
            </MenuCategory>
          </li>

          {/* help center link */}
          {isHelpLive && (
            <li>
              <MenuLinkWrapper>
                <MenuLink
                  track={[TRACK_CAT, 'Help']}
                  href={getInternalHref('/help')}
                  dataTestId="navigation-help"
                  useInternalNavigation={isFrodor}
                >
                  {t('FRODOR_NAVBAR_HELP', 'Help', '')}
                </MenuLink>
              </MenuLinkWrapper>
            </li>
          )}

          {USE_SEARCH && (
            <RequireFeatureFlag
              flag="s4a-frodor-test"
              fallback={<></>}
              waiting={<></>}
            >
              <li>
                <MenuLink
                  track={[TRACK_CAT, 'Search']}
                  href={getInternalHref('/search')}
                  dataTestId="navigation-search"
                >
                  <IconSearch />
                </MenuLink>
              </li>
            </RequireFeatureFlag>
          )}
        </ul>
      </LinksArea>

      <ActionsArea>
        <ul>
          <NavBarLanguageItem />
          {showLogin && (
            <StyledListItemNoMargin color={navBarTextColor}>
              <LoginButton
                buttonSize="sm"
                data-testid="root-login"
                onClick={() => {
                  onLinkClick({
                    href: 'https://accounts.spotify.com/login',
                    external: true,
                    trackingAttributes: [TRACK_CAT, 'Log In'],
                  });
                  login();
                }}
              >
                {t('FRODOR_NAVBAR_LOGIN', 'Log in', '')}
              </LoginButton>
            </StyledListItemNoMargin>
          )}
          {showLogout && (
            <StyledListItemNoMargin color={navBarTextColor}>
              <LoginButton
                buttonSize="sm"
                data-testid="root-logout"
                onClick={() => {
                  onLinkClick({
                    href: 'https://accounts.spotify.com/logout',
                    external: true,
                    trackingAttributes: [TRACK_CAT, 'Log In'],
                  });
                  logout();
                }}
              >
                {t('FRODOR_NAVBAR_LOGOUT', 'Log out', '')}
              </LoginButton>
            </StyledListItemNoMargin>
          )}
          {showClaim && (
            <StyledListItemWithMargin color={navBarTextColor}>
              <ClaimButton
                dataTestId="navigation-claim"
                track={[TRACK_CAT, `Get Access (${userState})`]}
                href={getInternalHref('/claim')}
                useInternalNavigation={isFrodor}
              >
                {t('FRODOR_NAVBAR_GET_ACCESS', 'Get access', '')}
              </ClaimButton>
            </StyledListItemWithMargin>
          )}
          {showDashboard && (
            <StyledListItemWithMargin color={navBarTextColor}>
              <ClaimButton
                track={[TRACK_CAT, 'Dashboard']}
                href={getInternalHref('/c')}
                useInternalNavigation={isFrodor}
                dataTestId="navigation-dashboard"
              >
                {t('FRODOR_NAVBAR_DASHBOARD', 'Dashboard', '')}
              </ClaimButton>
            </StyledListItemWithMargin>
          )}
        </ul>
      </ActionsArea>

      <MenuButtonArea customTextColor={navBarTextColor}>
        <OpenSideMenuButton
          css="padding-right: 0px;"
          buttonSize="md"
          iconOnly={IconMenu}
          tabIndex={0}
          onKeyDown={(e: React.KeyboardEvent) => {
            if (e.key === ' ' || e.key === 'Enter') {
              e.preventDefault();
              if (sideMenuOpen) {
                closeSideMenu();
              } else {
                openSideMenu();
              }
            }
          }}
          onClick={() => openSideMenu()}
          data-testid="navigation-sidemenu-trigger"
          aria-label={t(
            'FRODOR_NAV_RESPONSIVE_OPEN',
            'Open navigation menu',
            'open navigation menu in responsive display',
          )}
        />
      </MenuButtonArea>

      {/* Nav side menu */}
      {sideMenuOpen && (
        <Modal>
          <NavSideMenu userState={userState} onClose={() => closeSideMenu()} />
          <Backdrop onClick={() => closeSideMenu()} />
        </Modal>
      )}
    </NavBarWrapper>
  ) : (
    <Wait />
  );
};
